import { connect, useSelector } from 'react-redux';
import './chartStyle.css';
import SliderWithInput from '../SliderWithInput';
import { FormControl, MenuItem, Popover, Select } from '@mui/material';
import { updateCardControls } from '../../../redux/ChartPoperties/ChartControlsActions';
import { useState } from 'react';
import { ColorResult, SketchPicker } from 'react-color';
import { SelectComponentStyle, menuItemStyle } from '../Labels/SnakeyLabelOptions';
import { RootState } from '../../../redux';
import { useDispatch } from 'react-redux';

const CardStyle = ({
  iconColor,
  fromDashboard = false,
}: {
  iconColor?: string;
  fromDashboard?: boolean;
}) => {
  const dispatch = useDispatch();
  const { chartControls, tabTileProps } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
  }));

  var propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;
  var cardStyleOptions: any = chartControls.properties[propKey].cardControls;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const borderStyle: string[] = ['dashed', 'solid', 'dotted', 'double', 'groove', 'ridge'];
  const fontStyle: string[] = ['italic', 'normal'];

  const disabledStyle: React.CSSProperties = fromDashboard
    ? { opacity: 0.5, pointerEvents: 'none' }
    : {};

  return (
    <div className="optionsInfo">
      <>
        <div
          className="optionDescription"
          style={{ marginTop: '0', color: iconColor ? iconColor : '#404040' }}
        >
          Font Size
        </div>
        <SliderWithInput
          percent={false}
          sliderValue={cardStyleOptions.fontSize}
          sliderMinMax={{ min: 20, max: 100, step: 1 }}
          changeValue={(value) => {
            dispatch(updateCardControls(propKey, 'fontSize', value));
          }}
        />
      </>
      <div style={disabledStyle}>
        <div
          className="optionDescription"
          style={{ marginTop: '10px', color: iconColor ? iconColor : '#404040' }}
        >
          Border Thickness
        </div>
        <SliderWithInput
          percent={true}
          sliderValue={cardStyleOptions.borderTickness}
          sliderMinMax={{ min: 1, max: 50, step: 1 }}
          changeValue={(value) => {
            dispatch(updateCardControls(propKey, 'borderTickness', value));
          }}
        />
        <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
          Border Radius
        </div>
        <SliderWithInput
          percent={true}
          sliderValue={cardStyleOptions.borderRadius}
          sliderMinMax={{ min: 0, max: 100, step: 1 }}
          changeValue={(value) => {
            dispatch(updateCardControls(propKey, 'borderRadius', value));
          }}
        />
        <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
          Border Style
        </div>

        <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
          <Select
            value={cardStyleOptions.dashStyle}
            variant="outlined"
            onChange={(e) => {
              dispatch(updateCardControls(propKey, 'dashStyle', e.target.value));
            }}
            sx={{
              ...SelectComponentStyle,
              color: iconColor ? iconColor : '#404040',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: iconColor ? iconColor : '#2bb9bb', // Set focused border color
              },
              '& .MuiSvgIcon-root': { color: iconColor ? iconColor : '#404040' },
              '& .MuiSelect-select': { color: iconColor ? iconColor : '#404040' },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: 'rgba(43, 185, 187, 0.1) !important', // Force background color
                  },
                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: 'rgba(43, 185, 187, 0.2) !important', // Change hover state for selected item
                  },
                },
              },
            }}
          >
            {borderStyle.map((item: string) => {
              return (
                <MenuItem value={item} key={item} sx={menuItemStyle}>
                  {item}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </div>
      <div
        className="optionDescription"
        style={{ paddingTop: '0', marginTop: '8px', color: iconColor ? iconColor : '#404040' }}
      >
        Font Style
      </div>

      <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
        <Select
          value={cardStyleOptions.fontStyle}
          variant="outlined"
          onChange={(e) => {
            dispatch(updateCardControls(propKey, 'fontStyle', e.target.value));
          }}
          sx={{
            ...SelectComponentStyle,
            color: iconColor ? iconColor : '#404040',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: iconColor ? iconColor : '#2bb9bb', // Set focused border color
            },
            '& .MuiSvgIcon-root': { color: iconColor ? iconColor : '#404040' },
            '& .MuiSelect-select': { color: iconColor ? iconColor : '#404040' },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root.Mui-selected': {
                  backgroundColor: 'rgba(43, 185, 187, 0.1) !important', // Force background color
                },
                '& .MuiMenuItem-root.Mui-selected:hover': {
                  backgroundColor: 'rgba(43, 185, 187, 0.2) !important', // Change hover state for selected item
                },
              },
            },
          }}
        >
          {fontStyle.map((item: string) => {
            return (
              <MenuItem value={item} key={item} sx={menuItemStyle}>
                {item}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      <div style={disabledStyle}>
        <div
          className="optionDescription"
          style={{ marginTop: '5px', color: iconColor ? iconColor : '#404040' }}
        >
          Border Color
        </div>
        <div style={{ marginBottom: '15px' }}>
          <div
            style={{
              height: '1.25rem',
              width: '25%',
              backgroundColor: cardStyleOptions.borderColor,
              color: cardStyleOptions.borderColor,
              border: '2px solid darkgray',
              marginLeft: '12px',
              marginRight: 'auto',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              setAnchorEl(e.currentTarget);
            }}
          >
            {'  '}
          </div>
        </div>
      </div>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div>
          <SketchPicker
            color={cardStyleOptions.borderColor}
            className="sketchPicker"
            width="16rem"
            onChangeComplete={(color: ColorResult) => {
              dispatch(updateCardControls(propKey, 'borderColor', color.hex));
            }}
            onChange={(color: ColorResult) =>
              dispatch(updateCardControls(propKey, 'borderColor', color.hex))
            }
            disableAlpha
          />
        </div>
      </Popover>
    </div>
  );
};

export default CardStyle;
